import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CircleHelp, Mail, Settings } from 'lucide-react';
import NotificationsBell from '../notifications/NotificationsBell';
import { NotificationPersona } from '../../services/notifications/notificationService';

type SidebarUtilityStripProps = {
  isCollapsed: boolean;
  persona: NotificationPersona;
  notificationsPath: string;
  inboxPath: string;
  settingsPath?: string;
};

const iconButtonClass =
  'inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900';

const activeIconButtonClass =
  'inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-rowing-blue transition-colors';

const disabledIconButtonClass =
  'inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 opacity-50 cursor-not-allowed';

function pathMatches(pathname: string, target: string): boolean {
  return pathname === target || pathname.startsWith(`${target}/`);
}

const SidebarUtilityStrip: React.FC<SidebarUtilityStripProps> = ({
  isCollapsed,
  persona,
  notificationsPath,
  inboxPath,
  settingsPath,
}) => {
  const { pathname } = useLocation();
  const notificationsActive = pathMatches(pathname, notificationsPath);
  const inboxActive = pathMatches(pathname, inboxPath);
  const settingsActive = settingsPath ? pathMatches(pathname, settingsPath) : false;

  return (
    <div
      className={`flex ${
        isCollapsed
          ? 'flex-col items-center gap-0.5 px-0.5'
          : 'flex-row items-center justify-between gap-0.5 px-0.5'
      }`}
      role="toolbar"
      aria-label="Account utilities"
    >
      <NotificationsBell
        persona={persona}
        centerPath={notificationsPath}
        variant="sidebar"
        sidebarCollapsed={isCollapsed}
        active={notificationsActive}
      />

      <Link
        to={inboxPath}
        aria-label="Inbox"
        title="Inbox"
        aria-current={inboxActive ? 'page' : undefined}
        className={inboxActive ? activeIconButtonClass : iconButtonClass}
      >
        <Mail className="h-5 w-5" aria-hidden />
      </Link>

      <button
        type="button"
        disabled
        aria-label="Help (coming soon)"
        title="Help coming soon"
        className={disabledIconButtonClass}
      >
        <CircleHelp className="h-5 w-5" aria-hidden />
      </button>

      {settingsPath ? (
        <Link
          to={settingsPath}
          aria-label="Settings"
          title="Settings"
          aria-current={settingsActive ? 'page' : undefined}
          className={settingsActive ? activeIconButtonClass : iconButtonClass}
        >
          <Settings className="h-5 w-5" aria-hidden />
        </Link>
      ) : (
        <button
          type="button"
          disabled
          aria-label="Settings (unavailable)"
          title="Settings unavailable"
          className={disabledIconButtonClass}
        >
          <Settings className="h-5 w-5" aria-hidden />
        </button>
      )}
    </div>
  );
};

export default SidebarUtilityStrip;
